import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Settings2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../contexts/ThemeContext";
import type {
  Candle,
  Timeframe,
  TradingPosition,
  TradingIndicatorPreset,
  TradingIndicatorSettingsSnapshot,
} from "../../types/api";
import * as marketService from "../../services/marketService";
import { Skeleton } from "../ui/skeleton";
import {
  ColorType,
  CrosshairMode,
  AreaSeries,
  BaselineSeries,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type CandlestickData,
  type HistogramData,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";

interface ProfessionalCandlestickChartProps {
  symbol: string;
  timeframe?: Timeframe;
  height?: number;
  positions?: TradingPosition[];
  onTimeframeChange?: (timeframe: Timeframe) => void;
  userId?: number | null;
}

type ChartView = "candles" | "line" | "area";

const CHART_SYNAPSE_DARK = {
  background: "#0b1120",
  panel: "#111827",
  panelBorder: "#1f2937",
  grid: "#1f2937",
  text: "#94a3b8",
  textMuted: "#64748b",
  accent: "#22d3ee",
  accentSoft: "rgba(34,211,238,0.35)",
  accentFade: "rgba(34,211,238,0.02)",
  up: "#34d399",
  down: "#f87171",
} as const;

/**
 * Palette claire pour le canvas (lightweight-charts ne suit pas le CSS).
 */
const CHART_THEME_LIGHT = {
  background: "#f8fafc",
  panel: "#f1f5f9",
  panelBorder: "#e2e8f0",
  grid: "#e2e8f0",
  text: "#475569",
  textMuted: "#64748b",
  accent: "#0891b2",
  accentSoft: "rgba(8,145,178,0.22)",
  accentFade: "rgba(8,145,178,0.04)",
  up: "#059669",
  down: "#dc2626",
} as const;

function getChartTheme(theme: "light" | "dark") {
  return theme === "dark" ? CHART_SYNAPSE_DARK : CHART_THEME_LIGHT;
}

/**
 * Composant de graphique en chandeliers professionnel style TradingView/Synapse.
 * Mémoïsé pour éviter les re-renders quand le rafraîchissement du terminal
 * (ordres, historique, soldes) met à jour la page sans changer symbol/timeframe/positions.
 */
const ProfessionalCandlestickChartInner = ({
  symbol,
  timeframe = "1h",
  height = 500,
  positions = [],
  onTimeframeChange,
  userId,
}: ProfessionalCandlestickChartProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const chartTheme = getChartTheme(theme);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [previousPrice, setPreviousPrice] = useState<number | null>(null);
  const [chartView, setChartView] = useState<ChartView>("candles");
  const [showMa7, setShowMa7] = useState(true);
  const [showMa25, setShowMa25] = useState(true);
  const [showMa99, setShowMa99] = useState(false);
  const [showRsi, setShowRsi] = useState(false);
  const [showMacd, setShowMacd] = useState(false);
  const [showBb, setShowBb] = useState(false);
  const [isIndicatorPanelOpen, setIsIndicatorPanelOpen] = useState(false);
  const [indicatorTab, setIndicatorTab] = useState<"main" | "sub">("main");
  const [ma7Period, setMa7Period] = useState(7);
  const [ma25Period, setMa25Period] = useState(25);
  const [ma99Period, setMa99Period] = useState(99);
  const [rsiPeriod, setRsiPeriod] = useState(14);
  const [bbPeriod, setBbPeriod] = useState(20);
  const [bbStd, setBbStd] = useState(2);
  const [macdFast, setMacdFast] = useState(12);
  const [macdSlow, setMacdSlow] = useState(26);
  const [macdSignal, setMacdSignal] = useState(9);
  const [ma7Color, setMa7Color] = useState("#22d3ee");
  const [ma25Color, setMa25Color] = useState("#a855f7");
  const [ma99Color, setMa99Color] = useState("#f59e0b");
  const [maLineWidth, setMaLineWidth] = useState<1 | 2 | 3 | 4>(1);
  const [indicatorPreset, setIndicatorPreset] = useState("custom");
  const [indicatorPresets, setIndicatorPresets] = useState<
    TradingIndicatorPreset[]
  >([]);
  const [presetName, setPresetName] = useState("");
  const [hoveredCandle, setHoveredCandle] = useState<{
    open: number;
    high: number;
    low: number;
    close: number;
  } | null>(null);
  const [hoveredRsiValue, setHoveredRsiValue] = useState<number | null>(null);
  const [hoveredMacdValues, setHoveredMacdValues] = useState<{
    macd: number | null;
    signal: number | null;
    hist: number | null;
  } | null>(null);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const didHydrateRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  /** Après le premier setData, on utilise update() pour la dernière barre (temps réel). */
  const lastCandleCountRef = useRef(0);

  const { user, updateTradingIndicators } = useAuth();

  const storageKey = `trading-indicators-${userId ?? "guest"}`;

  const buildPresetSettings = useCallback(
    (): TradingIndicatorSettingsSnapshot => ({
      chartView,
      showMa7,
      showMa25,
      showMa99,
      showRsi,
      showMacd,
      showBb,
      ma7Period,
      ma25Period,
      ma99Period,
      rsiPeriod,
      bbPeriod,
      bbStd,
      macdFast,
      macdSlow,
      macdSignal,
      ma7Color,
      ma25Color,
      ma99Color,
      maLineWidth,
    }),
    [
      chartView,
      showMa7,
      showMa25,
      showMa99,
      showRsi,
      showMacd,
      showBb,
      ma7Period,
      ma25Period,
      ma99Period,
      rsiPeriod,
      bbPeriod,
      bbStd,
      macdFast,
      macdSlow,
      macdSignal,
      ma7Color,
      ma25Color,
      ma99Color,
      maLineWidth,
    ]
  );

  const isCustomPresetSelected = indicatorPresets.some(
    (preset) => preset.name === indicatorPreset
  );

  const applyIndicatorSettings = (
    saved: Partial<TradingIndicatorSettingsSnapshot> & {
      preset?: string;
      presets?: TradingIndicatorPreset[];
    }
  ) => {
    if (
      saved.chartView === "candles" ||
      saved.chartView === "line" ||
      saved.chartView === "area"
    ) {
      setChartView(saved.chartView);
    }
    if (typeof saved.showMa7 === "boolean") setShowMa7(saved.showMa7);
    if (typeof saved.showMa25 === "boolean") setShowMa25(saved.showMa25);
    if (typeof saved.showMa99 === "boolean") setShowMa99(saved.showMa99);
    if (typeof saved.showRsi === "boolean") setShowRsi(saved.showRsi);
    if (typeof saved.showMacd === "boolean") setShowMacd(saved.showMacd);
    if (typeof saved.showBb === "boolean") setShowBb(saved.showBb);
    if (typeof saved.ma7Period === "number") setMa7Period(saved.ma7Period);
    if (typeof saved.ma25Period === "number") setMa25Period(saved.ma25Period);
    if (typeof saved.ma99Period === "number") setMa99Period(saved.ma99Period);
    if (typeof saved.rsiPeriod === "number") setRsiPeriod(saved.rsiPeriod);
    if (typeof saved.bbPeriod === "number") setBbPeriod(saved.bbPeriod);
    if (typeof saved.bbStd === "number") setBbStd(saved.bbStd);
    if (typeof saved.macdFast === "number") setMacdFast(saved.macdFast);
    if (typeof saved.macdSlow === "number") setMacdSlow(saved.macdSlow);
    if (typeof saved.macdSignal === "number") setMacdSignal(saved.macdSignal);
    if (typeof saved.ma7Color === "string") setMa7Color(saved.ma7Color);
    if (typeof saved.ma25Color === "string") setMa25Color(saved.ma25Color);
    if (typeof saved.ma99Color === "string") setMa99Color(saved.ma99Color);
    if (
      saved.maLineWidth === 1 ||
      saved.maLineWidth === 2 ||
      saved.maLineWidth === 3 ||
      saved.maLineWidth === 4
    ) {
      setMaLineWidth(saved.maLineWidth);
    }
    if (typeof saved.preset === "string") setIndicatorPreset(saved.preset);
    if (Array.isArray(saved.presets)) {
      setIndicatorPresets(
        saved.presets.filter(
          (preset): preset is TradingIndicatorPreset =>
            Boolean(preset) &&
            typeof preset.name === "string" &&
            typeof preset.settings === "object"
        )
      );
    }
  };

  useEffect(() => {
    if (userId && user?.tradingIndicatorSettings) {
      applyIndicatorSettings(user.tradingIndicatorSettings);
      didHydrateRef.current = true;
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<Record<string, any>>;
      applyIndicatorSettings(saved);
      didHydrateRef.current = true;
    } catch {
      // ignore invalid local storage
    }
  }, [storageKey, userId, user?.tradingIndicatorSettings]);

  useEffect(() => {
    if (!userId || !didHydrateRef.current) return;
    setSaveStatus("saving");
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      void updateTradingIndicators({
        settings: {
          ...buildPresetSettings(),
          preset: indicatorPreset,
          presets: indicatorPresets,
        },
      })
        .then(() => {
          setSaveStatus("saved");
          if (saveStatusTimeoutRef.current) {
            clearTimeout(saveStatusTimeoutRef.current);
          }
          saveStatusTimeoutRef.current = setTimeout(() => {
            setSaveStatus("idle");
          }, 2000);
        })
        .catch(() => {
          setSaveStatus("error");
          if (saveStatusTimeoutRef.current) {
            clearTimeout(saveStatusTimeoutRef.current);
          }
          saveStatusTimeoutRef.current = setTimeout(() => {
            setSaveStatus("idle");
          }, 3000);
        });
    }, 600);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
    };
  }, [
    userId,
    indicatorPreset,
    indicatorPresets,
    chartView,
    showMa7,
    showMa25,
    showMa99,
    showRsi,
    showMacd,
    showBb,
    ma7Period,
    ma25Period,
    ma99Period,
    rsiPeriod,
    bbPeriod,
    bbStd,
    macdFast,
    macdSlow,
    macdSignal,
    ma7Color,
    ma25Color,
    ma99Color,
    maLineWidth,
    updateTradingIndicators,
    buildPresetSettings,
  ]);

  useEffect(() => {
    try {
      const payload = {
        ...buildPresetSettings(),
        preset: indicatorPreset,
        presets: indicatorPresets,
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // ignore quota errors
    }
  }, [
    storageKey,
    chartView,
    showMa7,
    showMa25,
    showMa99,
    showRsi,
    showMacd,
    showBb,
    ma7Period,
    ma25Period,
    ma99Period,
    rsiPeriod,
    bbPeriod,
    bbStd,
    macdFast,
    macdSlow,
    macdSignal,
    ma7Color,
    ma25Color,
    ma99Color,
    maLineWidth,
    indicatorPreset,
    indicatorPresets,
    buildPresetSettings,
  ]);

  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const lineSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const areaSeriesRef = useRef<ISeriesApi<"Area"> | null>(null);
  const ma7SeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const ma25SeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const ma99SeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const rsiSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const rsiUpperBandRef = useRef<ISeriesApi<"Baseline"> | null>(null);
  const rsiLowerBandRef = useRef<ISeriesApi<"Baseline"> | null>(null);
  const macdSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const macdSignalSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const macdHistogramRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const bbUpperSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const bbMiddleSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const bbLowerSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const markersRef = useRef<ReturnType<
    typeof createSeriesMarkers<Time>
  > | null>(null);

  // Calculer les données AVANT les early returns (fix hooks error)
  const candleData = useMemo(() => {
    if (candles.length === 0) return [];
    return candles.map(
      (c): CandlestickData => ({
        time: (c.timestamp > 1_000_000_000_000
          ? Math.floor(c.timestamp / 1000)
          : c.timestamp) as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })
    );
  }, [candles]);

  const volumeData = useMemo(() => {
    if (candles.length === 0) return [];
    return candles.map(
      (c): HistogramData => ({
        time: (c.timestamp > 1_000_000_000_000
          ? Math.floor(c.timestamp / 1000)
          : c.timestamp) as UTCTimestamp,
        value: c.volume,
        // Couleurs Synapse pour les volumes (vert/rouge selon direction)
        color: c.close >= c.open ? chartTheme.up : chartTheme.down,
      })
    );
  }, [candles, chartTheme]);

  const closeLineData = useMemo(() => {
    if (candles.length === 0) return [];
    return candles.map(
      (c): LineData => ({
        time: (c.timestamp > 1_000_000_000_000
          ? Math.floor(c.timestamp / 1000)
          : c.timestamp) as UTCTimestamp,
        value: c.close,
      })
    );
  }, [candles]);

  const computeMa = useCallback(
    (period: number): LineData[] => {
      if (candles.length === 0) return [];
      const values = candles.map((c) => c.close);
      const result: LineData[] = [];
      for (let i = 0; i < values.length; i++) {
        if (i + 1 < period) {
          result.push({
            time: (candles[i].timestamp > 1_000_000_000_000
              ? Math.floor(candles[i].timestamp / 1000)
              : candles[i].timestamp) as UTCTimestamp,
            value: values[i],
          });
          continue;
        }
        const slice = values.slice(i + 1 - period, i + 1);
        const avg =
          slice.reduce((sum, v) => sum + v, 0) / Math.max(1, slice.length);
        result.push({
          time: (candles[i].timestamp > 1_000_000_000_000
            ? Math.floor(candles[i].timestamp / 1000)
            : candles[i].timestamp) as UTCTimestamp,
          value: Number(avg.toFixed(4)),
        });
      }
      return result;
    },
    [candles]
  );

  const ma7Data = useMemo(
    () => computeMa(Math.max(2, ma7Period)),
    [computeMa, ma7Period]
  );
  const ma25Data = useMemo(
    () => computeMa(Math.max(2, ma25Period)),
    [computeMa, ma25Period]
  );
  const ma99Data = useMemo(
    () => computeMa(Math.max(2, ma99Period)),
    [computeMa, ma99Period]
  );

  const computeEma = (values: number[], period: number): number[] => {
    if (values.length === 0) return [];
    const k = 2 / (period + 1);
    const ema: number[] = [];
    let prev = values[0];
    ema.push(prev);
    for (let i = 1; i < values.length; i++) {
      const next = values[i] * k + prev * (1 - k);
      ema.push(next);
      prev = next;
    }
    return ema;
  };

  const macdData = useMemo(() => {
    if (candles.length === 0) {
      return { macd: [], signal: [], histogram: [] as HistogramData[] };
    }
    const values = candles.map((c) => c.close);
    const fast = Math.max(2, macdFast);
    const slow = Math.max(fast + 1, macdSlow);
    const signalPeriod = Math.max(2, macdSignal);
    const ema12 = computeEma(values, fast);
    const ema26 = computeEma(values, slow);
    const macdLine = values.map((_, i) => ema12[i] - ema26[i]);
    const signalLine = computeEma(macdLine, signalPeriod);
    const histogram = macdLine.map((val, i) => val - signalLine[i]);

    const macd = macdLine.map(
      (value, i): LineData => ({
        time: (candles[i].timestamp > 1_000_000_000_000
          ? Math.floor(candles[i].timestamp / 1000)
          : candles[i].timestamp) as UTCTimestamp,
        value: Number(value.toFixed(4)),
      })
    );
    const signal = signalLine.map(
      (value, i): LineData => ({
        time: (candles[i].timestamp > 1_000_000_000_000
          ? Math.floor(candles[i].timestamp / 1000)
          : candles[i].timestamp) as UTCTimestamp,
        value: Number(value.toFixed(4)),
      })
    );
    const hist = histogram.map(
      (value, i): HistogramData => ({
        time: (candles[i].timestamp > 1_000_000_000_000
          ? Math.floor(candles[i].timestamp / 1000)
          : candles[i].timestamp) as UTCTimestamp,
        value: Number(value.toFixed(4)),
        color: value >= 0 ? chartTheme.up : chartTheme.down,
      })
    );

    return { macd, signal, histogram: hist };
  }, [candles, macdFast, macdSlow, macdSignal, chartTheme]);

  const rsiData = useMemo(() => {
    if (candles.length === 0) return [];
    const values = candles.map((c) => c.close);
    const period = Math.max(2, rsiPeriod);
    const rsi: LineData[] = [];
    let gain = 0;
    let loss = 0;
    for (let i = 1; i < values.length; i++) {
      const change = values[i] - values[i - 1];
      if (i <= period) {
        gain += Math.max(0, change);
        loss += Math.max(0, -change);
      } else {
        gain = (gain * (period - 1) + Math.max(0, change)) / period;
        loss = (loss * (period - 1) + Math.max(0, -change)) / period;
      }
      const rs = loss === 0 ? 100 : gain / loss;
      const value = 100 - 100 / (1 + rs);
      rsi.push({
        time: (candles[i].timestamp > 1_000_000_000_000
          ? Math.floor(candles[i].timestamp / 1000)
          : candles[i].timestamp) as UTCTimestamp,
        value: Number(value.toFixed(2)),
      });
    }
    return rsi;
  }, [candles, rsiPeriod]);

  const rsiBandUpperData = useMemo(() => {
    if (candles.length === 0) return [];
    return candles.map(
      (c): LineData => ({
        time: (c.timestamp > 1_000_000_000_000
          ? Math.floor(c.timestamp / 1000)
          : c.timestamp) as UTCTimestamp,
        value: 100,
      })
    );
  }, [candles]);

  const rsiBandLowerData = useMemo(() => {
    if (candles.length === 0) return [];
    return candles.map(
      (c): LineData => ({
        time: (c.timestamp > 1_000_000_000_000
          ? Math.floor(c.timestamp / 1000)
          : c.timestamp) as UTCTimestamp,
        value: 0,
      })
    );
  }, [candles]);

  const bbData = useMemo(() => {
    if (candles.length === 0) {
      return { upper: [], middle: [], lower: [] as LineData[] };
    }
    const period = Math.max(2, bbPeriod);
    const stdMultiplier = Math.max(0.1, bbStd);
    const values = candles.map((c) => c.close);
    const upper: LineData[] = [];
    const middle: LineData[] = [];
    const lower: LineData[] = [];

    for (let i = 0; i < values.length; i++) {
      const slice = values.slice(Math.max(0, i + 1 - period), i + 1);
      const mean = slice.reduce((sum, v) => sum + v, 0) / slice.length;
      const variance =
        slice.reduce((sum, v) => sum + (v - mean) ** 2, 0) / slice.length;
      const std = Math.sqrt(variance);
      const time = (
        candles[i].timestamp > 1_000_000_000_000
          ? Math.floor(candles[i].timestamp / 1000)
          : candles[i].timestamp
      ) as UTCTimestamp;
      middle.push({ time, value: Number(mean.toFixed(4)) });
      upper.push({
        time,
        value: Number((mean + stdMultiplier * std).toFixed(4)),
      });
      lower.push({
        time,
        value: Number((mean - stdMultiplier * std).toFixed(4)),
      });
    }

    return { upper, middle, lower };
  }, [candles, bbPeriod, bbStd]);

  const lastRsiValue = useMemo(() => {
    if (!rsiData.length) return null;
    return rsiData[rsiData.length - 1].value;
  }, [rsiData]);

  const lastMacdValues = useMemo(() => {
    if (!macdData.macd.length) return null;
    const macd = macdData.macd[macdData.macd.length - 1]?.value ?? null;
    const signal = macdData.signal[macdData.signal.length - 1]?.value ?? null;
    const hist =
      macdData.histogram[macdData.histogram.length - 1]?.value ?? null;
    return { macd, signal, hist };
  }, [macdData]);

  // Dernière bougie pour afficher par défaut
  const lastCandle = useMemo(() => {
    return candles.length > 0 ? candles[candles.length - 1] : null;
  }, [candles]);

  // Charger les données initiales
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await marketService.getCandles(symbol, timeframe, 200);
        setCandles(data);
        if (data.length > 0) {
          const newPrice = data[data.length - 1].close;
          // Sauvegarder l'ancien prix avant de le mettre à jour
          if (currentPrice !== null) {
            setPreviousPrice(currentPrice);
          }
          setCurrentPrice(newPrice);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des chandeliers:", error);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  // WebSocket pour les mises à jour en temps réel des bougies
  useEffect(() => {
    if (!symbol || loading) return;

    // Normaliser le symbole pour Binance
    const normalizeSymbol = (sym: string): string => {
      let normalized = sym.replace("/", "").toLowerCase();
      if (normalized.endsWith("usd") && normalized !== "usdt") {
        normalized = normalized.replace("usd", "usdt");
      }
      return normalized;
    };

    // Mapper le timeframe vers l'interval Binance
    const timeframeToBinanceInterval = (tf: Timeframe): string => {
      const mapping: Record<Timeframe, string> = {
        "1m": "1m",
        "5m": "5m",
        "15m": "15m",
        "1h": "1h",
        "4h": "4h",
        "1d": "1d",
      };
      return mapping[tf] || "1h";
    };

    const normalized = normalizeSymbol(symbol);
    const binanceInterval = timeframeToBinanceInterval(timeframe);
    const wsUrl = `wss://stream.binance.com:9443/ws/${normalized}@kline_${binanceInterval}`;

    let ws: WebSocket | null = null;

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log(`WebSocket kline connecté pour ${symbol} (${timeframe})`);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const kline = data.k;

          if (!kline) return;

          const newCandle: Candle = {
            timestamp: kline.t, // Open time
            open: parseFloat(kline.o),
            high: parseFloat(kline.h),
            low: parseFloat(kline.l),
            close: parseFloat(kline.c),
            volume: parseFloat(kline.v),
          };

          setCandles((prevCandles) => {
            if (prevCandles.length === 0) return [newCandle];

            const lastCandle = prevCandles[prevCandles.length - 1];

            // Si c'est la même bougie (même timestamp), la mettre à jour en temps réel
            // Cela permet de voir la bougie se former en direct même si elle n'est pas encore fermée
            if (lastCandle.timestamp === newCandle.timestamp) {
              const updated = [...prevCandles];
              updated[updated.length - 1] = newCandle;
              return updated;
            } else if (newCandle.timestamp > lastCandle.timestamp) {
              // Nouvelle bougie (nouveau timestamp) - l'ajouter
              const updated = [...prevCandles, newCandle];
              // Garder seulement les 200 dernières bougies
              return updated.slice(-200);
            } else {
              // Timestamp antérieur (ne devrait pas arriver normalement), ignorer
              return prevCandles;
            }
          });

          // Mettre à jour le prix actuel en temps réel
          if (currentPrice !== null) {
            setPreviousPrice(currentPrice);
          }
          setCurrentPrice(newCandle.close);
        } catch (error) {
          console.error("Erreur lors du parsing des données kline WebSocket:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("Erreur WebSocket kline:", error);
      };

      ws.onclose = (event) => {
        console.log(`WebSocket kline fermé pour ${symbol}`, event.code, event.reason);
      };
    } catch (error) {
      console.error("Erreur lors de la création du WebSocket kline:", error);
    }

    return () => {
      if (ws) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe, loading]);

  const hoveredCandleData = hoveredCandle || lastCandle;
  const priceChange =
    currentPrice && previousPrice ? currentPrice - previousPrice : null;
  const priceChangePercent =
    currentPrice && previousPrice && previousPrice !== 0
      ? ((currentPrice - previousPrice) / previousPrice) * 100
      : null;

  // Init chart + resize handling
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el || loading) return;

    // Nettoyer si déjà monté
    if (chartRef.current) {
      lastCandleCountRef.current = 0;
      chartRef.current.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      lineSeriesRef.current = null;
      areaSeriesRef.current = null;
      ma7SeriesRef.current = null;
      ma25SeriesRef.current = null;
      ma99SeriesRef.current = null;
      rsiSeriesRef.current = null;
      rsiUpperBandRef.current = null;
      rsiLowerBandRef.current = null;
      macdSeriesRef.current = null;
      macdSignalSeriesRef.current = null;
      macdHistogramRef.current = null;
      bbUpperSeriesRef.current = null;
      bbMiddleSeriesRef.current = null;
      bbLowerSeriesRef.current = null;
      volumeSeriesRef.current = null;
    }

    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: {
          type: ColorType.Solid,
          color: chartTheme.background,
        },
        textColor: chartTheme.text,
        fontSize: 12,
        fontFamily:
          '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: chartTheme.grid, style: 0 },
        horzLines: { color: chartTheme.grid, style: 0 },
      },
      rightPriceScale: {
        borderColor: chartTheme.panelBorder,
        scaleMargins: { top: 0.1, bottom: 0.25 },
      },
      timeScale: {
        borderColor: chartTheme.panelBorder,
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: {
          color: chartTheme.text,
          width: 1,
          style: 0,
          labelBackgroundColor: chartTheme.panel,
        },
        horzLine: {
          color: chartTheme.text,
          width: 1,
          style: 0,
          labelBackgroundColor: chartTheme.panel,
        },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      handleScale: {
        mouseWheel: true,
        pinch: true,
        axisPressedMouseMove: { time: true, price: true },
      },
    });

    // Couleurs Synapse pour les chandeliers
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: chartTheme.up,
      downColor: chartTheme.down,
      borderUpColor: chartTheme.up,
      borderDownColor: chartTheme.down,
      wickUpColor: chartTheme.up,
      wickDownColor: chartTheme.down,
      priceFormat: {
        type: "price",
        precision: 2,
        minMove: 0.01,
      },
    });

    const lineSeries = chart.addSeries(LineSeries, {
      color: chartTheme.accent,
      lineWidth: 2,
    });

    const areaSeries = chart.addSeries(AreaSeries, {
      lineColor: chartTheme.accent,
      topColor: chartTheme.accentSoft,
      bottomColor: chartTheme.accentFade,
      lineWidth: 2,
    });

    const ma7Series = chart.addSeries(LineSeries, {
      color: ma7Color,
      lineWidth: maLineWidth,
    });

    const ma25Series = chart.addSeries(LineSeries, {
      color: ma25Color,
      lineWidth: maLineWidth,
    });

    const ma99Series = chart.addSeries(LineSeries, {
      color: ma99Color,
      lineWidth: maLineWidth,
    });

    const rsiSeries = chart.addSeries(
      LineSeries,
      {
        color: chartTheme.accent,
        lineWidth: 1,
      },
      1
    );

    const rsiUpperBand = chart.addSeries(
      BaselineSeries,
      {
        baseValue: { type: "price", price: 70 },
        topFillColor1: "rgba(239,68,68,0.08)",
        topFillColor2: "rgba(239,68,68,0.015)",
        topLineColor: "rgba(0,0,0,0)",
        bottomFillColor1: "rgba(0,0,0,0)",
        bottomFillColor2: "rgba(0,0,0,0)",
        bottomLineColor: "rgba(0,0,0,0)",
      },
      1
    );

    const rsiLowerBand = chart.addSeries(
      BaselineSeries,
      {
        baseValue: { type: "price", price: 30 },
        topFillColor1: "rgba(0,0,0,0)",
        topFillColor2: "rgba(0,0,0,0)",
        topLineColor: "rgba(0,0,0,0)",
        bottomFillColor1: "rgba(16,185,129,0.08)",
        bottomFillColor2: "rgba(16,185,129,0.015)",
        bottomLineColor: "rgba(0,0,0,0)",
      },
      1
    );

    // RSI levels (30/70)
    rsiSeries.createPriceLine({
      price: 70,
      color: "rgba(239,68,68,0.6)",
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: false,
      title: "RSI 70",
    });
    rsiSeries.createPriceLine({
      price: 30,
      color: "rgba(16,185,129,0.6)",
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: false,
      title: "RSI 30",
    });

    const macdSeries = chart.addSeries(
      LineSeries,
      {
        color: chartTheme.accent,
        lineWidth: 1,
      },
      2
    );

    // MACD baseline
    macdSeries.createPriceLine({
      price: 0,
      color: "rgba(148,163,184,0.7)",
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: false,
      title: "0",
    });

    const macdSignalSeries = chart.addSeries(
      LineSeries,
      {
        color: "#a855f7",
        lineWidth: 1,
      },
      2
    );

    const macdHistogram = chart.addSeries(
      HistogramSeries,
      {
        priceFormat: { type: "volume" },
      },
      2
    );

    const bbUpper = chart.addSeries(LineSeries, {
      color: "#f59e0b",
      lineWidth: 1,
    });
    const bbMiddle = chart.addSeries(LineSeries, {
      color: "#94a3b8",
      lineWidth: 1,
    });
    const bbLower = chart.addSeries(LineSeries, {
      color: "#f59e0b",
      lineWidth: 1,
    });

    // Volume series avec couleurs individuelles par barre (Synapse)
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "",
      // Pas de couleur globale - chaque barre aura sa propre couleur depuis volumeData
    });

    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
      borderColor: chartTheme.panelBorder,
      entireTextOnly: false,
    });

    candleSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.1, bottom: 0.25 },
      borderColor: chartTheme.panelBorder,
      entireTextOnly: false,
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    lineSeriesRef.current = lineSeries;
    areaSeriesRef.current = areaSeries;
    ma7SeriesRef.current = ma7Series;
    ma25SeriesRef.current = ma25Series;
    ma99SeriesRef.current = ma99Series;
    rsiSeriesRef.current = rsiSeries;
    rsiUpperBandRef.current = rsiUpperBand;
    rsiLowerBandRef.current = rsiLowerBand;
    macdSeriesRef.current = macdSeries;
    macdSignalSeriesRef.current = macdSignalSeries;
    macdHistogramRef.current = macdHistogram;
    bbUpperSeriesRef.current = bbUpper;
    bbMiddleSeriesRef.current = bbMiddle;
    bbLowerSeriesRef.current = bbLower;
    volumeSeriesRef.current = volumeSeries;

    // Initialiser avec les données disponibles immédiatement
    if (candleData.length > 0) {
      candleSeries.setData(candleData);
    }
    if (closeLineData.length > 0) {
      lineSeries.setData(closeLineData);
      areaSeries.setData(closeLineData);
    }
    if (ma7Data.length > 0) {
      ma7Series.setData(ma7Data);
    }
    if (ma25Data.length > 0) {
      ma25Series.setData(ma25Data);
    }
    if (ma99Data.length > 0) {
      ma99Series.setData(ma99Data);
    }
    if (rsiData.length > 0) {
      rsiSeries.setData(rsiData);
    }
    if (rsiBandUpperData.length > 0) {
      rsiUpperBand.setData(rsiBandUpperData);
      rsiLowerBand.setData(rsiBandLowerData);
    }
    if (macdData.macd.length > 0) {
      macdSeries.setData(macdData.macd);
      macdSignalSeries.setData(macdData.signal);
      macdHistogram.setData(macdData.histogram);
    }
    if (bbData.upper.length > 0) {
      bbUpper.setData(bbData.upper);
      bbMiddle.setData(bbData.middle);
      bbLower.setData(bbData.lower);
    }
    if (volumeData.length > 0) {
      volumeSeries.setData(volumeData);
    }

    markersRef.current = createSeriesMarkers(candleSeries, [], {
      // look pro: petits markers, pas trop visibles
      markers: {
        textColor:
          theme === "dark"
            ? "rgba(255,255,255,0.85)"
            : "rgba(15,23,42,0.85)",
      },
    } as any);

    // Crosshair move → legend OHLC + indicators
    const onCrosshairMove = (param: any) => {
      const series = candleSeriesRef.current;
      if (!series) return;
      const data = param?.seriesData?.get(series) as
        | CandlestickData
        | undefined;
      if (!data) {
        setHoveredCandle(null);
        setHoveredRsiValue(null);
        setHoveredMacdValues(null);
        return;
      }
      setHoveredCandle({
        open: data.open,
        high: data.high,
        low: data.low,
        close: data.close,
      });

      const rsiSeries = rsiSeriesRef.current;
      const rsiPoint = rsiSeries
        ? (param?.seriesData?.get(rsiSeries) as LineData | undefined)
        : undefined;
      setHoveredRsiValue(
        typeof rsiPoint?.value === "number" ? rsiPoint.value : null
      );

      const macdSeries = macdSeriesRef.current;
      const macdSignalSeries = macdSignalSeriesRef.current;
      const macdHistogram = macdHistogramRef.current;
      const macdPoint = macdSeries
        ? (param?.seriesData?.get(macdSeries) as LineData | undefined)
        : undefined;
      const signalPoint = macdSignalSeries
        ? (param?.seriesData?.get(macdSignalSeries) as LineData | undefined)
        : undefined;
      const histPoint = macdHistogram
        ? (param?.seriesData?.get(macdHistogram) as HistogramData | undefined)
        : undefined;
      setHoveredMacdValues({
        macd: typeof macdPoint?.value === "number" ? macdPoint.value : null,
        signal:
          typeof signalPoint?.value === "number" ? signalPoint.value : null,
        hist: typeof histPoint?.value === "number" ? histPoint.value : null,
      });
    };
    chart.subscribeCrosshairMove(onCrosshairMove);

    // ResizeObserver (autoSize gère, mais on force un fitContent propre)
    const ro = new ResizeObserver(() => {
      chart.timeScale().fitContent();
    });
    ro.observe(el);

    return () => {
      lastCandleCountRef.current = 0;
      ro.disconnect();
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      markersRef.current?.detach();
      markersRef.current = null;
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      lineSeriesRef.current = null;
      areaSeriesRef.current = null;
      ma7SeriesRef.current = null;
      ma25SeriesRef.current = null;
      ma99SeriesRef.current = null;
      rsiSeriesRef.current = null;
      rsiUpperBandRef.current = null;
      rsiLowerBandRef.current = null;
      macdSeriesRef.current = null;
      macdSignalSeriesRef.current = null;
      macdHistogramRef.current = null;
      bbUpperSeriesRef.current = null;
      bbMiddleSeriesRef.current = null;
      bbLowerSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, loading, theme]);

  // Set series data + markers when candles/positions change
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    const lineSeries = lineSeriesRef.current;
    const areaSeries = areaSeriesRef.current;
    const ma7Series = ma7SeriesRef.current;
    const ma25Series = ma25SeriesRef.current;
    const ma99Series = ma99SeriesRef.current;
    const rsiSeries = rsiSeriesRef.current;
    const rsiUpperBand = rsiUpperBandRef.current;
    const rsiLowerBand = rsiLowerBandRef.current;
    const macdSeries = macdSeriesRef.current;
    const macdSignalSeries = macdSignalSeriesRef.current;
    const macdHistogram = macdHistogramRef.current;
    const bbUpper = bbUpperSeriesRef.current;
    const bbMiddle = bbMiddleSeriesRef.current;
    const bbLower = bbLowerSeriesRef.current;
    const chart = chartRef.current;
    if (
      !candleSeries ||
      !volumeSeries ||
      !chart ||
      !lineSeries ||
      !areaSeries ||
      !ma7Series ||
      !ma25Series ||
      !ma99Series ||
      !rsiSeries ||
      !rsiUpperBand ||
      !rsiLowerBand ||
      !macdSeries ||
      !macdSignalSeries ||
      !macdHistogram ||
      !bbUpper ||
      !bbMiddle ||
      !bbLower
    ) {
      return;
    }

    const isInitial = lastCandleCountRef.current === 0;
    const hasData = candleData.length > 0;

    if (!hasData) return;

    if (isInitial) {
      // Premier chargement : setData complet
      candleSeries.setData(candleData);
      volumeSeries.setData(volumeData);
      if (closeLineData.length > 0) {
        lineSeries.setData(closeLineData);
        areaSeries.setData(closeLineData);
      }
      if (ma7Data.length > 0) ma7Series.setData(ma7Data);
      if (ma25Data.length > 0) ma25Series.setData(ma25Data);
      if (ma99Data.length > 0) ma99Series.setData(ma99Data);
      if (rsiData.length > 0) {
        rsiSeries.setData(rsiData);
        rsiUpperBand.setData(rsiBandUpperData);
        rsiLowerBand.setData(rsiBandLowerData);
      }
      if (macdData.macd.length > 0) {
        macdSeries.setData(macdData.macd);
        macdSignalSeries.setData(macdData.signal);
        macdHistogram.setData(macdData.histogram);
      }
      if (bbData.upper.length > 0) {
        bbUpper.setData(bbData.upper);
        bbMiddle.setData(bbData.middle);
        bbLower.setData(bbData.lower);
      }
      lastCandleCountRef.current = candleData.length;
      chart.timeScale().fitContent();
    } else {
      // Mise à jour temps réel : update() sur la dernière barre uniquement
      const lastCandle = candleData[candleData.length - 1];
      const lastVolume = volumeData[volumeData.length - 1];
      const lastLine = closeLineData[closeLineData.length - 1];
      candleSeries.update(lastCandle);
      volumeSeries.update(lastVolume);
      lineSeries.update(lastLine);
      areaSeries.update(lastLine);
      if (ma7Data.length > 0) ma7Series.update(ma7Data[ma7Data.length - 1]);
      if (ma25Data.length > 0) ma25Series.update(ma25Data[ma25Data.length - 1]);
      if (ma99Data.length > 0) ma99Series.update(ma99Data[ma99Data.length - 1]);
      if (rsiData.length > 0) {
        rsiSeries.update(rsiData[rsiData.length - 1]);
        rsiUpperBand.update(rsiBandUpperData[rsiBandUpperData.length - 1]);
        rsiLowerBand.update(rsiBandLowerData[rsiBandLowerData.length - 1]);
      }
      if (macdData.macd.length > 0) {
        macdSeries.update(macdData.macd[macdData.macd.length - 1]);
        macdSignalSeries.update(macdData.signal[macdData.signal.length - 1]);
        macdHistogram.update(macdData.histogram[macdData.histogram.length - 1]);
      }
      if (bbData.upper.length > 0) {
        bbUpper.update(bbData.upper[bbData.upper.length - 1]);
        bbMiddle.update(bbData.middle[bbData.middle.length - 1]);
        bbLower.update(bbData.lower[bbData.lower.length - 1]);
      }
      lastCandleCountRef.current = candleData.length;
    }

    // Dernier prix + label (style Synapse)
    const lastValueOptions = {
      lastValueVisible: true,
      priceLineVisible: true,
      priceLineColor: chartTheme.text,
    };
    candleSeries.applyOptions({
      ...lastValueOptions,
      visible: chartView === "candles",
    });
    lineSeries.applyOptions({
      ...lastValueOptions,
      visible: chartView === "line",
    });
    areaSeries.applyOptions({
      ...lastValueOptions,
      visible: chartView === "area",
    });
    ma7Series.applyOptions({
      visible: showMa7,
      color: ma7Color,
      lineWidth: maLineWidth,
    });
    ma25Series.applyOptions({
      visible: showMa25,
      color: ma25Color,
      lineWidth: maLineWidth,
    });
    ma99Series.applyOptions({
      visible: showMa99,
      color: ma99Color,
      lineWidth: maLineWidth,
    });
    rsiSeries.applyOptions({ visible: showRsi });
    rsiUpperBand.applyOptions({ visible: showRsi });
    rsiLowerBand.applyOptions({ visible: showRsi });
    macdSeries.applyOptions({ visible: showMacd });
    macdSignalSeries.applyOptions({ visible: showMacd });
    macdHistogram.applyOptions({ visible: showMacd });
    bbUpper.applyOptions({ visible: showBb });
    bbMiddle.applyOptions({ visible: showBb });
    bbLower.applyOptions({ visible: showBb });

    const panes = chart.panes();
    const hasRsi = showRsi;
    const hasMacd = showMacd;
    const mainFactor = hasRsi || hasMacd ? 0.7 : 1;
    const rsiFactor = hasRsi ? 0.15 : 0.001;
    const macdFactor = hasMacd ? 0.15 : 0.001;
    if (panes[0]) panes[0].setStretchFactor(mainFactor);
    if (panes[1]) panes[1].setStretchFactor(rsiFactor);
    if (panes[2]) panes[2].setStretchFactor(macdFactor);

    // Markers (positions) : entrée
    const markers: SeriesMarker<Time>[] =
      positions
        .filter((p) => p.marketSymbol === symbol)
        .map((p) => ({
          // Pas de openedAt dans le type → on ancre le marker sur la dernière bougie
          time: candleData.length
            ? (candleData[candleData.length - 1].time as Time)
            : (Math.floor(Date.now() / 1000) as UTCTimestamp),
          position: "aboveBar",
          color: p.pnlValue >= 0 ? chartTheme.up : chartTheme.down,
          shape: "arrowDown",
          text: `Entry ${p.averageEntryPrice.toFixed(2)}`,
        })) ?? [];

    markersRef.current?.setMarkers(markers);

    // fitContent uniquement au premier setData (voir isInitial plus haut)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candleData,
    closeLineData,
    volumeData,
    ma7Data,
    ma25Data,
    ma99Data,
    rsiData,
    rsiBandUpperData,
    rsiBandLowerData,
    macdData,
    bbData,
    positions,
    symbol,
    chartView,
    showMa7,
    showMa25,
    showMa99,
    showRsi,
    showMacd,
    showBb,
    ma7Color,
    ma25Color,
    ma99Color,
    maLineWidth,
    theme,
    chartTheme,
  ]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full rounded-lg border border-border bg-background dark:border-white/10 dark:bg-slate-950"
      style={{ height }}>
      <>
        {/* En-tête style Synapse */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-4 py-2.5 backdrop-blur dark:border-white/10 dark:bg-slate-950/90">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-4 flex-wrap">
        <div>
                <h3 className="text-sm font-semibold text-foreground">{symbol}</h3>
                {currentPrice !== null && (
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <p className="text-lg font-bold text-foreground">
              {currentPrice.toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 8,
              })}
            </p>
                    {priceChange !== null && priceChangePercent !== null && (
                      <span
                        className={`text-sm font-medium ${
                          priceChange >= 0
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-red-600 dark:text-red-400"
                        }`}>
                        {priceChange >= 0 ? "+" : ""}
                        {priceChange.toFixed(2)} (
                        {priceChangePercent >= 0 ? "+" : ""}
                        {priceChangePercent.toFixed(2)}%)
                      </span>
          )}
        </div>
                )}
              </div>
              {hoveredCandleData && (
                <div className="hidden md:flex items-center gap-4 text-xs font-mono">
                  <span className="text-muted-foreground">
                    O{" "}
                    <span className="text-foreground">
                      {hoveredCandleData.open.toFixed(2)}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    H{" "}
                    <span className="text-foreground">
                      {hoveredCandleData.high.toFixed(2)}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    L{" "}
                    <span className="text-foreground">
                      {hoveredCandleData.low.toFixed(2)}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    C{" "}
                    <span className="text-foreground">
                      {hoveredCandleData.close.toFixed(2)}
                    </span>
                  </span>
                </div>
              )}
              {(showRsi || showMacd) && (
                <div className="hidden lg:flex items-center gap-3 text-xs">
                  {showRsi && (hoveredRsiValue ?? lastRsiValue) !== null && (
                    <span className="text-[#22d3ee]">
                      {t("trading.indicators.rsiLabel")}{" "}
                      {(hoveredRsiValue ?? lastRsiValue)?.toFixed(2)}
                    </span>
                  )}
                  {showMacd && (hoveredMacdValues ?? lastMacdValues) && (
                    <>
                      <span className="text-cyan-400">
                        {t("trading.indicators.macdLabel")}{" "}
                        {(hoveredMacdValues ?? lastMacdValues)?.macd?.toFixed(
                          2
                        )}
                      </span>
                      <span className="text-[#a855f7]">
                        {t("trading.indicators.signalLabel")}{" "}
                        {(hoveredMacdValues ?? lastMacdValues)?.signal?.toFixed(
                          2
                        )}
                      </span>
                      <span className="text-muted-foreground">
                        {t("trading.indicators.histLabel")}{" "}
                        {(hoveredMacdValues ?? lastMacdValues)?.hist?.toFixed(
                          2
                        )}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 ml-4 items-end">
            <div className="flex gap-1 items-center">
              {(["1m", "5m", "15m", "1h", "4h", "1d"] as Timeframe[]).map(
                (tf) => (
            <button
              key={tf}
              onClick={() => onTimeframeChange?.(tf)}
                    className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                timeframe === tf
                        ? "bg-cyan-400 text-slate-900 font-semibold"
                        : "bg-muted text-muted-foreground hover:bg-muted/80 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                    }`}>
                    {tf}
                  </button>
                )
              )}
              <button
                onClick={() => setIsIndicatorPanelOpen(true)}
                className="ml-1 inline-flex items-center gap-1 rounded bg-muted px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted/80 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                aria-label={t("trading.indicators.openAria")}>
                <Settings2 className="h-3.5 w-3.5" />
                {t("trading.indicators.openButton")}
              </button>
            </div>
            <div className="flex gap-1">
              {(["candles", "line", "area"] as ChartView[]).map((view) => (
                <button
                  key={view}
                  onClick={() => setChartView(view)}
                  className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                    chartView === view
                      ? "bg-cyan-400 text-slate-900"
                      : "bg-muted text-muted-foreground hover:bg-muted/80 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                  }`}>
                  {view === "candles"
                    ? t("trading.chartView.candles")
                    : view === "line"
                    ? t("trading.chartView.line")
                    : t("trading.chartView.area")}
            </button>
          ))}
            </div>
        </div>
      </div>

      {/* Graphique principal */}
        <div className="absolute inset-0 pt-[56px]">
          <div ref={chartContainerRef} className="h-full w-full" />
        </div>
      </>

      {loading && (
        <div className="absolute inset-0 z-20">
          <Skeleton className="h-full w-full" style={{ height }} />
        </div>
      )}

      {!loading && candles.length === 0 && (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <p className="text-muted-foreground">{t("trading.chartNoData")}</p>
        </div>
      )}

      {isIndicatorPanelOpen && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-3xl rounded-2xl border border-border bg-card p-6 text-foreground shadow-2xl dark:border-white/10 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-border pb-3 dark:border-white/10">
              <div className="flex gap-4 text-sm">
                <button
                  onClick={() => setIndicatorTab("main")}
                  className={`pb-1 ${
                    indicatorTab === "main"
                      ? "border-b-2 border-cyan-400 font-semibold"
                      : "text-muted-foreground"
                  }`}>
                  {t("trading.indicators.mainTab")}
                </button>
                <button
                  onClick={() => setIndicatorTab("sub")}
                  className={`pb-1 ${
                    indicatorTab === "sub"
                      ? "border-b-2 border-cyan-400 font-semibold"
                      : "text-muted-foreground"
                  }`}>
                  {t("trading.indicators.subTab")}
                </button>
              </div>
              <button
                onClick={() => setIsIndicatorPanelOpen(false)}
                className="rounded-lg p-2 hover:bg-muted dark:hover:bg-white/5"
                aria-label={t("trading.indicators.closeAria")}>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="text-muted-foreground/80">
                {t("trading.indicators.presetsLabel")}
              </span>
              {userId && saveStatus !== "idle" && (
                <span
                  className={`ml-2 rounded px-2 py-1 text-[11px] ${
                    saveStatus === "saving"
                      ? "bg-muted text-muted-foreground dark:bg-white/10 dark:text-white/70"
                      : saveStatus === "saved"
                      ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-200"
                      : "bg-red-500/20 text-red-700 dark:text-red-200"
                  }`}>
                  {saveStatus === "saving"
                    ? t("trading.indicators.status.saving")
                    : saveStatus === "saved"
                    ? t("trading.indicators.status.saved")
                    : t("trading.indicators.status.error")}
                </span>
              )}
              <button
                onClick={() => {
                  setShowMa7(true);
                  setShowMa25(true);
                  setShowMa99(true);
                  setShowBb(false);
                  setShowRsi(false);
                  setShowMacd(false);
                  setIndicatorPreset("classic");
                }}
                className="rounded bg-muted/60 px-3 py-1 hover:bg-muted dark:bg-white/5 dark:hover:bg-white/10">
                {t("trading.indicators.presets.classic")}
              </button>
              <button
                onClick={() => {
                  setShowMa7(false);
                  setShowMa25(false);
                  setShowMa99(false);
                  setShowBb(false);
                  setShowRsi(true);
                  setShowMacd(true);
                  setIndicatorTab("sub");
                  setIndicatorPreset("momentum");
                }}
                className="rounded bg-muted/60 px-3 py-1 hover:bg-muted dark:bg-white/5 dark:hover:bg-white/10">
                {t("trading.indicators.presets.momentum")}
              </button>
              <button
                onClick={() => {
                  setShowMa7(false);
                  setShowMa25(true);
                  setShowMa99(false);
                  setShowBb(true);
                  setShowRsi(false);
                  setShowMacd(false);
                  setIndicatorTab("main");
                  setIndicatorPreset("bands");
                }}
                className="rounded bg-muted/60 px-3 py-1 hover:bg-muted dark:bg-white/5 dark:hover:bg-white/10">
                {t("trading.indicators.presets.bands")}
              </button>
              <select
                value={indicatorPreset}
                onChange={(e) => {
                  const next = e.target.value;
                  const selected = indicatorPresets.find(
                    (preset) => preset.name === next
                  );
                  if (selected?.settings) {
                    applyIndicatorSettings(selected.settings);
                  }
                  setIndicatorPreset(next);
                }}
                className="rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:bg-black/30 dark:border-transparent">
                <option value="custom">
                  {t("trading.indicators.presets.custom")}
                </option>
                {indicatorPresets.map((preset) => (
                  <option key={preset.name} value={preset.name}>
                    {preset.name}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder={t("trading.indicators.presetNamePlaceholder")}
                  className="w-32 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground dark:bg-black/30 dark:border-transparent dark:text-white dark:placeholder:text-white/40"
                />
                <button
                  onClick={() => {
                    const name = presetName.trim();
                    if (!name) return;
                    const settings = buildPresetSettings();
                    setIndicatorPresets((prev) => {
                      const next = prev.filter((p) => p.name !== name);
                      return [...next, { name, settings }];
                    });
                    setIndicatorPreset(name);
                    setPresetName("");
                  }}
                  className="rounded bg-muted px-3 py-1 text-xs hover:bg-muted/80 dark:bg-white/10 dark:hover:bg-white/20">
                  {t("trading.indicators.save")}
                </button>
                {isCustomPresetSelected && (
                  <button
                    onClick={() => {
                      const nextName = presetName.trim();
                      if (!nextName) return;
                      setIndicatorPresets((prev) =>
                        prev
                          .filter((preset) => preset.name !== nextName)
                          .map((preset) =>
                            preset.name === indicatorPreset
                              ? { ...preset, name: nextName }
                              : preset
                          )
                      );
                      setIndicatorPreset(nextName);
                      setPresetName("");
                    }}
                    className="rounded bg-muted px-3 py-1 text-xs hover:bg-muted/80 dark:bg-white/10 dark:hover:bg-white/20">
                    {t("trading.indicators.rename")}
                  </button>
                )}
                {isCustomPresetSelected && (
                  <button
                    onClick={() => {
                      setIndicatorPresets((prev) =>
                        prev.filter((preset) => preset.name !== indicatorPreset)
                      );
                      setIndicatorPreset("custom");
                    }}
                    className="rounded bg-red-500/20 px-3 py-1 text-xs text-red-700 hover:bg-red-500/30 dark:text-red-200">
                    {t("trading.indicators.delete")}
                  </button>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-muted-foreground">
                  {t("trading.indicators.maThicknessLabel")}
                </span>
                <select
                  value={maLineWidth}
                  onChange={(e) =>
                    setMaLineWidth(Number(e.target.value) as 1 | 2 | 3 | 4)
                  }
                  className="rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:bg-black/30 dark:border-transparent">
                  {[1, 2, 3].map((w) => (
                    <option key={w} value={w}>
                      {w}px
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              {indicatorTab === "main" ? (
                <div className="space-y-3">
                  <p className="text-xs uppercase text-muted-foreground">
                    {t("trading.indicators.mainSectionLabel")}
                  </p>
                  <label className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5">
                    <span>MA1</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={ma7Color}
                        onChange={(e) => setMa7Color(e.target.value)}
                        className="h-6 w-6 rounded border border-border bg-transparent dark:border-white/20"
                        aria-label="Couleur MA1"
                      />
                      <input
                        type="number"
                        min={2}
                        value={ma7Period}
                        onChange={(e) =>
                          setMa7Period(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="checkbox"
                        checked={showMa7}
                        onChange={() => setShowMa7((prev) => !prev)}
                      />
                    </div>
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5">
                    <span>MA2</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={ma25Color}
                        onChange={(e) => setMa25Color(e.target.value)}
                        className="h-6 w-6 rounded border border-border bg-transparent dark:border-white/20"
                        aria-label="Couleur MA2"
                      />
                      <input
                        type="number"
                        min={2}
                        value={ma25Period}
                        onChange={(e) =>
                          setMa25Period(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="checkbox"
                        checked={showMa25}
                        onChange={() => setShowMa25((prev) => !prev)}
                      />
            </div>
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5">
                    <span>MA3</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={ma99Color}
                        onChange={(e) => setMa99Color(e.target.value)}
                        className="h-6 w-6 rounded border border-border bg-transparent dark:border-white/20"
                        aria-label="Couleur MA3"
                      />
                      <input
                        type="number"
                        min={2}
                        value={ma99Period}
                        onChange={(e) =>
                          setMa99Period(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="checkbox"
                        checked={showMa99}
                        onChange={() => setShowMa99((prev) => !prev)}
                      />
              </div>
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5">
                    <span>Bollinger (BB)</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={2}
                        value={bbPeriod}
                        onChange={(e) =>
                          setBbPeriod(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={bbStd}
                        onChange={(e) => setBbStd(Number(e.target.value) || 2)}
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="checkbox"
                        checked={showBb}
                        onChange={() => setShowBb((prev) => !prev)}
                      />
              </div>
                  </label>
              </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs uppercase text-muted-foreground">
                    {t("trading.indicators.subSectionLabel")}
                  </p>
                  <label className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5">
                    <span>RSI</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={2}
                        value={rsiPeriod}
                        onChange={(e) =>
                          setRsiPeriod(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="checkbox"
                        checked={showRsi}
                        onChange={() => setShowRsi((prev) => !prev)}
                      />
              </div>
                  </label>
                  <label className="flex items-center justify-between rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5">
                    <span>MACD</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={2}
                        value={macdFast}
                        onChange={(e) =>
                          setMacdFast(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="number"
                        min={2}
                        value={macdSlow}
                        onChange={(e) =>
                          setMacdSlow(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="number"
                        min={2}
                        value={macdSignal}
                        onChange={(e) =>
                          setMacdSignal(Number(e.target.value) || 2)
                        }
                        className="w-16 rounded border border-border bg-muted px-2 py-1 text-xs text-foreground dark:border-transparent dark:bg-black/30"
                      />
                      <input
                        type="checkbox"
                        checked={showMacd}
                        onChange={() => setShowMacd((prev) => !prev)}
                      />
              </div>
                  </label>
            </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowMa7(true);
                  setShowMa25(true);
                  setShowMa99(false);
                  setShowBb(false);
                  setShowRsi(false);
                  setShowMacd(false);
                  setMa7Period(7);
                  setMa25Period(25);
                  setMa99Period(99);
                  setRsiPeriod(14);
                  setBbPeriod(20);
                  setBbStd(2);
                  setMacdFast(12);
                  setMacdSlow(26);
                  setMacdSignal(9);
                  setMa7Color("#22d3ee");
                  setMa25Color("#a855f7");
                  setMa99Color("#f59e0b");
                  setMaLineWidth(1);
                  setIndicatorPreset("default");
                }}
                className="rounded-lg bg-muted px-4 py-2 text-sm text-foreground hover:bg-muted/80 dark:bg-white/5 dark:text-white/80 dark:hover:bg-white/10">
                {t("trading.indicators.reset")}
              </button>
              <button
                onClick={() => setIsIndicatorPanelOpen(false)}
                className="rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-900">
                {t("trading.indicators.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ProfessionalCandlestickChart = memo(ProfessionalCandlestickChartInner);
